import { Box, Icon } from "@chakra-ui/react";
import type { BoxProps } from "@chakra-ui/react";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AppIcons } from "constants/icons";
import { ARTIFACT_TAG_RE, ARTIFACT_TAG_TYPE } from "modules/chat/utils/chatUtils";
import {
  ArtifactKind,
  getDataframeTagLinkPresentation,
  ARTIFACT_TAG_META,
} from "modules/artifacts/utils/artifactUtils";
import { ArtifactProto } from "build-ts/chat_session";

/**
 * Browsers (especially Chrome) inject zero-width chars (U+200B, U+FEFF)
 * around contentEditable="false" nodes. Strip them when reading back text.
 */
const ZERO_WIDTH_RE = /[\u200B\uFEFF]/g;

/**
 * Walk the contenteditable div and reconstruct the canonical raw text string,
 * including `[[workflow: name]]` tags, from the live DOM.
 *
 * This is the inverse of `buildNodes`. It is called:
 *  - on every `input` event, to derive the new `value` to hand to `onChange`
 *  - inside the `useEffect` sync guard, to decide whether a re-render is needed
 *
 * Node handling rules:
 *
 *  TEXT_NODE  → append its text content directly, stripping zero-width chars
 *               (Chrome inserts U+200B / U+FEFF around contentEditable=false nodes).
 *
 *  ELEMENT_NODE / BR  → convert to a newline character.
 *
 *  ELEMENT_NODE / DIV or P  → Chrome wraps each new line in a <div> (or <p>) when
 *                              the user presses Enter inside a contenteditable. Emit
 *                              a "\n" before recursing into any such block element
 *                              that follows already-written content, so that pressing
 *                              Enter reconstructs as a real newline in the value string.
 *
 *  ELEMENT_NODE with `data-tag`  → this is an workflow chip span. Its *visual*
 *                                   textContent says "Workflow: foo", but the raw
 *                                   value we want is the original `[[workflow: foo]]`
 *                                   stored in `el.dataset.tag`. We restore that tag
 *                                   verbatim and do NOT recurse into the chip's children,
 *                                   because the SVG icon and label text must not appear
 *                                   in the serialised output.
 *
 *  Any other ELEMENT_NODE  → recurse into its children.
 */
function serializeDiv(div: HTMLDivElement): string {
  let result = "";
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      result += (node.textContent ?? "").replace(ZERO_WIDTH_RE, "");
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName === "BR") {
        result += "\n";
      } else if (el.dataset.tag) {
        // Chip node — restore the raw tag, ignore visual textContent
        result += el.dataset.tag;
      } else {
        // Chrome inserts a <div> (sometimes <p>) per line when the user presses
        // Enter. Prepend a newline before the block's content whenever there is
        // already content to separate from, so the reconstructed value faithfully
        // represents multi-line input.
        if ((el.tagName === "DIV" || el.tagName === "P") && result.length > 0) {
          result += "\n";
        }
        Array.from(el.childNodes).forEach(walk);
      }
    }
  };
  Array.from(div.childNodes).forEach(walk);
  return result;
}

/** Records where a chip's React content should be portalled into. */
type ChipMount = { container: HTMLElement; label: string; type: string };

/**
 * Inner content of an artifact chip.
 * Rendered via a React portal so it stays in the main React tree.
 */
const ChipContent: React.FC<{
  label: string;
  type: string;
  lookupByName?: (name: string, kind?: ArtifactKind) => ArtifactProto | undefined;
}> = ({ label, type, lookupByName }) => {
  const meta = ARTIFACT_TAG_META[type] ?? ARTIFACT_TAG_META.workflow;
  const artifact =
    type === ARTIFACT_TAG_TYPE.dataframe ? lookupByName?.(label, ARTIFACT_TAG_TYPE.dataframe) : undefined;
  const icon =
    type === ARTIFACT_TAG_TYPE.dataframe ? getDataframeTagLinkPresentation(artifact, label).icon : AppIcons[meta.icon];
  return (
    <>
      <Icon as={icon} boxSize={4} display="inline" verticalAlign="middle" flexShrink={0} />
      {`\u00A0${meta.prefix}: ${label}`}
    </>
  );
};

/**
 * Turn raw text into an array of DOM nodes (text nodes + chip spans).
 * Each chip span is an empty container; the caller is responsible for
 * portalling React content into it via the `chips` collector.
 *
 * Recognises all artifact tag types: workflow, dataframe, chart, code.
 */
function buildNodes(text: string, chips: ChipMount[]): Node[] {
  const nodes: Node[] = [];
  const re = new RegExp(ARTIFACT_TAG_RE.source, "gi");
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(document.createTextNode(text.slice(lastIndex, match.index)));
    }

    const tagType = match[1].toLowerCase(); // "workflow" | "dataframe" | "chart" | "code"
    const raw = match[2].trim();
    // Workflow body is "darwinName|header.name" — extract the human-readable header.name
    // after the pipe as the display label. All other types use the body directly.
    const pipeIdx = tagType === "workflow" ? raw.indexOf("|") : -1;
    const displayLabel = pipeIdx !== -1 ? raw.slice(pipeIdx + 1).trim() : raw;

    const chip = document.createElement("span");
    chip.dataset.tag = match[0];
    chip.contentEditable = "false";
    chip.style.cssText =
      "background:var(--chakra-colors-purple-50);color:var(--chakra-colors-gray-800);border-radius:4px;" +
      "padding:1px 6px;font-weight:600;white-space:nowrap;display:inline-flex;align-items:center;gap:3px;";

    chips.push({ container: chip, label: displayLabel, type: tagType });
    nodes.push(chip);
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(document.createTextNode(text.slice(lastIndex)));
  } else if (nodes.length > 0) {
    // The text ended with a chip. Without a text node after it browsers
    // can't place the cursor past the chip, making it impossible to type
    // after it. An empty text node gives the cursor a valid landing spot.
    nodes.push(document.createTextNode(""));
  }

  return nodes;
}

/**
 * Move the browser text cursor (caret) to the very end of the contenteditable div.
 *
 * Why not just call `el.focus()`?
 * `focus()` alone restores the cursor to wherever it was when the element last lost
 * focus — which may be the beginning of the text. Using the Selection/Range API lets
 * us explicitly collapse the selection to the end of the node's contents, so the user
 * can immediately continue typing without having to click to reposition.
 *
 * Called by the imperative `focus()` handle (see `useImperativeHandle` below) whenever
 * a parent component programmatically focuses the textarea (e.g. after inserting a chip
 * via the mention menu, or when the chat input mounts).
 */
function placeCursorAtEnd(el: HTMLDivElement) {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * Imperative handle exposed to parent components via `ref`.
 *
 * Because this component is a contenteditable div (not a native <input> or
 * <textarea>), React's standard `ref.focus()` would only move browser focus
 * without repositioning the caret. The `focus` method here combines
 * `div.focus()` with `placeCursorAtEnd` so callers always get a usable cursor
 * position, not just visual focus.
 *
 * Usage:
 * ```tsx
 * const textareaRef = useRef<MessageInputHandle>(null);
 * // ...
 * textareaRef.current?.focus(); // focuses AND moves cursor to end
 * // ...
 * <MessageInput ref={textareaRef} ... />
 * ```
 */
export interface MessageInputHandle {
  focus: () => void;
  /**
   * Replace the `@query` token that precedes the cursor with `tag`.
   *
   * The method locates the last `@…` segment before the caret in the
   * serialized text, replaces it with the given tag string (plus a trailing
   * space so the user can continue typing), then syncs the controlled value
   * via `onChange` and repositions the caret to the end.
   */
  replaceAtMention: (tag: string) => void;
  /**
   * Append `tag` to the end of the current input content, then sync the
   * controlled value via `onChange` and reposition the caret to the end.
   *
   * Unlike `replaceAtMention`, this does not require an `@query` prefix — it
   * is used by the +Workflow add-menu so that chips are inserted via the same
   * direct DOM-mutation path as `@` mentions (no React state round-trip).
   */
  insertTag: (tag: string) => void;
}

export interface MessageInputProps extends Pick<
  BoxProps,
  "fontSize" | "fontWeight" | "px" | "py" | "pt" | "pb" | "minHeight" | "maxHeight" | "flex" | "w" | "id"
> {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  placeholder?: string;
  // When set and the input is empty, shows this text instead of the regular placeholder
  previewText?: string;
  isDisabled?: boolean;
  lookupByName?: (name: string, kind?: ArtifactKind) => ArtifactProto | undefined;
}

/**
 * A contenteditable div that renders [[workflow: name]] tokens as styled
 * purple chips. The raw value (including the tags) is always what is stored
 * and sent to the agent — the chips are purely visual DOM nodes.
 *
 * Unlike the old dual-layer (transparent textarea + backdrop div) approach,
 * the cursor never misaligns because there is only one layer: the cursor,
 * the text, and the chips all live in the same DOM element.
 */
const MessageInput = forwardRef<MessageInputHandle, MessageInputProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      placeholder,
      previewText,
      isDisabled,
      fontSize = "md",
      fontWeight,
      px,
      py,
      pt,
      pb,
      minHeight,
      maxHeight,
      flex,
      w,
      id,
      lookupByName,
    },
    ref
  ) => {
    const divRef = useRef<HTMLDivElement>(null);
    const [chipMounts, setChipMounts] = useState<ChipMount[]>([]);

    useImperativeHandle(ref, () => ({
      focus: () => {
        const div = divRef.current;
        if (!div) return;
        div.focus();
        placeCursorAtEnd(div);
      },
      replaceAtMention: (tag: string) => {
        const div = divRef.current;
        if (!div) return;
        // Work on the serialised text. Find the last '@' that starts an
        // unfinished mention (no whitespace between '@' and the cursor).
        const current = serializeDiv(div);
        const atIndex = current.lastIndexOf("@");
        if (atIndex === -1) return;
        const replaced = current.slice(0, atIndex) + tag + " ";
        // Rebuild the DOM from the new value (same path as the prop-sync effect).
        div.innerHTML = "";
        const mounts: ChipMount[] = [];
        const nodes = buildNodes(replaced, mounts);
        nodes.forEach(n => div.appendChild(n));
        setChipMounts(mounts);
        placeCursorAtEnd(div);
        onChange(replaced);
      },
      insertTag: (tag: string) => {
        const div = divRef.current;
        if (!div) return;
        const current = serializeDiv(div);
        const newValue = current ? `${current} ${tag} ` : `${tag} `;
        div.innerHTML = "";
        const mounts: ChipMount[] = [];
        const nodes = buildNodes(newValue, mounts);
        nodes.forEach(n => div.appendChild(n));
        setChipMounts(mounts);
        placeCursorAtEnd(div);
        onChange(newValue);
      },
    }));

    /**
     * Keep the DOM in sync with the controlled `value` prop.
     *
     * The guard (`serializeDiv(div) === value`) is critical: without it, every
     * keystroke would tear down and rebuild the DOM from scratch. That would:
     *   1. Reset the cursor to position 0 after every character.
     *   2. Cause a flicker as the browser repaints the element.
     *
     * When the user types, `handleInput` calls `onChange(serializeDiv(div))`,
     * which updates `value`. On the next render this effect fires, but because
     * the DOM already reflects what the user typed, the guard short-circuits and
     * the cursor is left untouched.
     *
     * A full rebuild IS performed when `value` changes from outside (e.g. the
     * parent resets the field, or injects a `[[workflow: name]]` chip programmatically).
     */
    useEffect(() => {
      const div = divRef.current;
      if (!div) return;
      if (serializeDiv(div) === value) return;

      div.innerHTML = "";
      const mounts: ChipMount[] = [];
      const nodes = buildNodes(value, mounts);
      nodes.forEach(n => div.appendChild(n));
      setChipMounts(mounts);
    }, [value]);

    const handleInput = () => {
      if (!divRef.current) return;
      onChange(serializeDiv(divRef.current));
    };

    // Strip rich-text formatting on paste — plain text only.
    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, text);
    };

    const effectivePt = pt ?? py;
    const effectivePb = pb ?? py;

    return (
      <>
        <Box
          ref={divRef}
          id={id}
          flex={flex}
          w={flex ? "100%" : w}
          contentEditable={!isDisabled}
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLDivElement>}
          onPaste={handlePaste}
          data-placeholder={previewText || placeholder}
          fontSize={fontSize}
          fontWeight={fontWeight}
          px={px}
          pt={effectivePt}
          pb={effectivePb}
          lineHeight="1.5"
          fontFamily="inherit"
          minH={minHeight}
          maxH={maxHeight}
          overflowY="auto"
          outline="none"
          textAlign="left"
          wordBreak="break-word"
          whiteSpace="pre-wrap"
          color={isDisabled ? "gray.400" : "gray.800"}
          cursor={isDisabled ? "not-allowed" : "text"}
          sx={{
            "&:empty::before": {
              content: "attr(data-placeholder)",
              color: "var(--chakra-colors-gray-400)",
              pointerEvents: "none",
            },
            caretColor: "var(--chakra-colors-gray-800)",
          }}
        />
        {chipMounts.map(({ container, label, type }, i) =>
          createPortal(<ChipContent label={label} type={type} lookupByName={lookupByName} />, container, `chip-${i}`)
        )}
      </>
    );
  }
);

export default MessageInput;
